import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import classnames from 'classnames'
import { useTranslation } from 'i18n/utils'

import { CardSection } from 'shared/components/CardSection'
import { LocationCard } from 'shared/components/LocationCard'
import TotalPrice from 'booking/components/flow-steps/ReviewPay/TotalPrice'
import PromoCode from 'shared/components/PromoCode'
import PaymentMethodSection from 'shared/components/PaymentMethodSection'
import { PriceToPay } from 'shared/components/EditBookingPriceBreakdown/PriceToPay'
import Button from 'shared/components/Button'
import { PersonalDetailsForm } from 'shared/components/PersonalDetailsForm'
import ErrorMessage from 'shared/components/ErrorMessage'

import { CUSTOMER_TYPE_INDIVIDUAL } from 'shared/constants'
import { GOOGLE_PAY_METHOD } from 'shared/transactions'
import featureToggles from 'shared/featureToggles'
import GooglePayButton from 'credit-cards/google-pay/components/GooglePayButton'

import { userSelector } from 'auth/selectors'
import { buyScanticket, redeemScanticketPromo, removeScanticketPromo } from 'scanticket/actions'
import { buyScanTicketPendingSelector, scanticketPromoCodePendingSelector } from 'scanticket/selectors'
import { guestUserCardVerifiedSelector } from 'booking/selectors'
import { ANALYTICS_FLOW_TYPE_SCAN_TICKET } from 'analytics/constants'
import { pixQrCodeDataSelector } from 'credit-cards/selectors'

import { TicketInfo } from './TicketInfo'

import styles from '../styles.module.scss'

const TicketReview = ({ locationName, ticket, successCallback, history }) => {
    const dispatch = useDispatch()
    const t = useTranslation()

    const [promoCode, setPromoCode] = React.useState('')
    const [successPromoCode, setSuccessPromoCode] = useState(false)
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null)
    const [personalDataFormValid, setPersonalDataFormValid] = useState(false)

    const pending = useSelector(buyScanTicketPendingSelector)
    const user = useSelector(userSelector)
    const promoPending = useSelector(scanticketPromoCodePendingSelector)
    const guestUserCardIsVerified = useSelector(guestUserCardVerifiedSelector)
    const pixQrCodeData = useSelector(pixQrCodeDataSelector)

    const [personalDataForm, setPersonalDataForm] = React.useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        documentType: CUSTOMER_TYPE_INDIVIDUAL,
    })

    const personalDataFormRef = React.useRef(null)

    const paymentMethods = user ? user.creditCards : guestUserCardIsVerified ? [guestUserCardIsVerified] : null

    const { amountDue, originalAmount, currencySymbol, isPromo } = ticket

    useEffect(() => {
        if (ticket?.discountCouponAvailable) {
            setSuccessPromoCode(true)
        } else {
            setSuccessPromoCode(false)
        }
    }, [ticket])

    useEffect(() => {
        if (pixQrCodeData) {
            history.push('/pix')
        }
    }, [pixQrCodeData])

    const onAddPromoCode = promoCode => dispatch(redeemScanticketPromo(promoCode))

    const onBuyNowClick = () => dispatch(buyScanticket(selectedPaymentMethod, successCallback, personalDataForm))

    const onChangePersonalDataForm = event => {
        const { name, value } = event.target
        setPersonalDataForm(formData => ({ ...formData, [name]: value }))
        validatePersonalDataForm()
    }

    const validatePersonalDataForm = async () => {
        if (personalDataFormRef?.current) {
            await personalDataFormRef.current.validateForm()
            const updatedState = personalDataFormRef.current.state
            setPersonalDataFormValid(updatedState.isValid)
        }
    }

    const submitDisabled = !selectedPaymentMethod || (!user && !personalDataFormValid) || pending === true

    return (
        <>
            <CardSection>
                <LocationCard locationName={locationName} />
            </CardSection>
            <CardSection>
                <div className={styles.productDetails}>
                    <TotalPrice
                        rate={{
                            currencySymbol,
                            rateName: t('scanticket.ticketinfo.ticket.name'),
                            amount: isPromo ? originalAmount : amountDue,
                        }}
                        discountedTotalPrice={isPromo && amountDue}
                    />
                    <TicketInfo ticket={ticket} />
                    <div className="d-flex justify-content-end">
                        <PromoCode
                            promoCodeValue={promoCode}
                            setRawPromoCode={setPromoCode}
                            onRemovePromoCode={() => dispatch(removeScanticketPromo())}
                            onAddPromoCode={onAddPromoCode}
                            removePromoCodeLabel={<T>booking.promocode.remove</T>}
                            btnLabel={<T>booking.promocode.apply</T>}
                            placeholder={<T>booking.hourly.flow.promocode.placeholder</T>}
                            error={promoPending?.message ?? ''}
                            success={successPromoCode}
                            btnStyle="primary"
                            disabled={promoPending === true}
                            isLoading={promoPending === true}
                        />
                    </div>
                </div>
                {!user && (
                    <div className={classnames(styles.guestUserDetails, 'mt-6 mb-4')}>
                        <h3 className="font-weight-bold text-uppercase">
                            <T>scanticket.ticketinfo.guest.details</T>
                        </h3>
                        <PersonalDetailsForm
                            formData={personalDataForm}
                            onChangeFormData={onChangePersonalDataForm}
                            personalDataFormRef={personalDataFormRef}
                        />
                    </div>
                )}
                <PaymentMethodSection
                    selectedPaymentMethod={selectedPaymentMethod}
                    setPaymentMethod={setSelectedPaymentMethod}
                    paymentMethods={paymentMethods}
                    userId={user?.id}
                    guestUserFlow={!user}
                    removeGuestUserCard={() => {}}
                    hasGooglePay={featureToggles.hasGooglePayEnabled}
                    analyticsFlowType={ANALYTICS_FLOW_TYPE_SCAN_TICKET}                
                />
                <div className="d-flex flex-column align-items-end mt-4">
                    <PriceToPay
                        rowStyles="d-flex align-items-center mb-2"
                        rate={{
                            amount: amountDue || originalAmount,
                            rateName: t('scanticket.ticketinfo.ticket.name'),
                            currencySymbol,
                        }}
                        label={<T>subscripition.purchase.pay.label.total</T>}
                        total={amountDue || originalAmount}
                    />
                    {selectedPaymentMethod?.id === GOOGLE_PAY_METHOD ? (
                        <GooglePayButton
                            disabled={submitDisabled}
                            isLoading={pending === true}
                            totalPrice={amountDue || originalAmount}
                            onClick={onBuyNowClick}
                            fallbackLabel={<T>booking.btn.buy.now</T>}
                        />
                    ) : (
                        <Button
                            isLoading={pending === true}
                            btnStyle="primary"
                            type="submit"
                            disabled={submitDisabled}
                            onClick={onBuyNowClick}>
                            <T>booking.btn.buy.now</T>
                        </Button>
                    )}
                </div>
                {pending instanceof Error && <ErrorMessage className="mt-4" messages={[pending.message]} />}
            </CardSection>
        </>
    )
}

export default TicketReview
